/**
 * Block Stream Buffer
 * Buffers text chunks, detects complete blocks, and renders formatted markdown
 *
 * PURPOSE:
 * - Buffer text until complete block detected (paragraph ends with \n\n, etc.)
 * - Force-flush incomplete buffer when component interrupts
 * - Parse markdown → sanitize → render with fade-in
 * - Merge consecutive list items into shared <ul>/<ol>
 * - Support staggered rendering via CSS animation-delay for programmatic messages
 *
 * ARCHITECTURE:
 * - Uses existing renderMarkdown() utility for consistent configuration
 * - Implements priority-ordered block detection patterns
 * - Handles smooth fade-in animations for progressive rendering
 * - CSS-based stagger: all blocks render immediately with animation-delay based on index
 *
 * @module BlockStreamBuffer
 */

import { renderMarkdown } from '../utils/markdownRenderer.js';
import { fadeIn } from '../utils/animation.js';
import { structuredBlockRenderers, structuredBlockHoldMs } from './structuredBlockRenderers.js';

export class BlockStreamBuffer {
  /**
   * @param {HTMLElement} targetContainer - DOM container for blocks
   * @param {Object} options - Configuration options
   * @param {number} options.fadeInDuration - Fade transition duration in ms (default: 300)
   * @param {string} options.fadeInEasing - CSS easing function (default: 'ease-out')
   * @param {number} options.slideDistance - Vertical slide distance in px (default: 8)
   * @param {number} options.staggerDelay - Delay between block animations in ms (default: 0, no stagger)
   * @param {number} options.minBlockSpacing - Minimum ms between block fade-in starts when staggerDelay === 0 (default: 150). Ensures burst-rendered blocks cascade instead of popping simultaneously. Only applies when staggerDelay === 0.
   */
  constructor(targetContainer, options = {}) {
    this.container = targetContainer;
    this.buffer = '';
    this.lastBlockType = null;
    this.lastListElement = null;

    // Extract the callback so it doesn't leak into this.config
    const { onBlockRender = null, ...renderOptions } = options;

    /**
     * Fires on EVERY rendered block or list item (fadeInBlock is also called
     * per list item from appendToExistingList) — NOT once per message. The
     * consumer flips the thinking indicator waiting→bars; repeat firing costs
     * one classList.contains. Do not assume once-per-message semantics.
     */
    this.onBlockRender = onBlockRender;

    this.config = {
      fadeInDuration: 300,
      fadeInEasing: 'ease-out',
      slideDistance: 8,
      staggerDelay: 0,
      minBlockSpacing: 150,
      ...renderOptions
    };

    // Track block count for CSS stagger animation-delay calculation
    this.blockIndex = 0;

    // Absolute performance.now() timestamp when the most recently scheduled
    // block will begin its fade-in. Used by the min-spacing path to enforce
    // a minimum cascade interval between burst-rendered blocks.
    this._lastBlockStart = null;

    // Running total (ms) of extra hold time contributed by self-timed
    // structured blocks (see structuredBlockHoldMs) rendered so far. Added on
    // top of every later block's own delay so text doesn't appear while a
    // preceding card (e.g. the memory-update card) is still animating.
    this._extraDelay = 0;
  }

  /**
   * Add text chunk to buffer
   * Processes and renders complete blocks immediately
   * @param {string} textChunk - Plain text (no markers)
   */
  addChunk(textChunk) {
    if (!textChunk) return;

    this.buffer += textChunk;
    this.processCompleteBlocks();
  }

  /**
   * Detect and render all complete blocks in buffer
   * Retains incomplete portion
   * For staggerDelay > 0, applies CSS animation-delay based on block index
   */
  processCompleteBlocks() {
    while (this.buffer.length > 0) {
      const block = this.detectNextBlock(this.buffer);

      if (block.complete) {
        this._renderBlock(block);
        this.buffer = block.remaining;
      } else {
        break; // Wait for more chunks
      }
    }
  }

  /**
   * Detect next complete block using priority-ordered patterns
   * @param {string} text - Text to analyze
   * @returns {Object} {complete: boolean, type: string, markdown: string, remaining: string}
   */
  detectNextBlock(text) {
    // 0. Structured content block: <tag-name>...</tag-name>
    //    Must start at beginning of buffer (after any leading whitespace/newlines)
    //    Only matches tags registered in the structuredBlockRenderers registry
    const structuredPattern = /^[\s\n]*<([a-z][a-z0-9-]*?)>([\s\S]*?)<\/\1>\n?/;
    const structuredMatch = text.match(structuredPattern);
    if (structuredMatch && structuredBlockRenderers.has(structuredMatch[1])) {
      return {
        complete: true,
        type: 'structured',
        tagName: structuredMatch[1],
        innerContent: structuredMatch[2],
        markdown: structuredMatch[0],
        remaining: text.slice(structuredMatch[0].length)
      };
    }

    // 0b. Incomplete structured tag guard — prevent paragraph detection from consuming it
    const incompleteStructuredPattern = /^[\s\n]*<([a-z][a-z0-9-]*?)>[\s\S]*$/;
    const incompleteMatch = text.match(incompleteStructuredPattern);
    if (incompleteMatch && structuredBlockRenderers.has(incompleteMatch[1])) {
      // Known tag started but not closed — wait for more chunks
      // Safety valve: if buffer is very large, the tag is likely malformed — flush as paragraph
      if (text.length > 10000) { // raised to accommodate base64-encoded proposal snapshots
        return {
          complete: true,
          type: 'paragraph',
          markdown: text,
          remaining: ''
        };
      }
      return { complete: false, type: null, markdown: '', remaining: text };
    }

    // 1. Code block: ```lang\n...\n```
    const codeBlockPattern = /^```[\s\S]*?\n```\n?/;
    const codeMatch = text.match(codeBlockPattern);
    if (codeMatch) {
      return {
        complete: true,
        type: 'code_block',
        markdown: codeMatch[0],
        remaining: text.slice(codeMatch[0].length)
      };
    }

    // 2. Heading: # Text\n
    const headingPattern = /^#{1,6} .+?\n/;
    const headingMatch = text.match(headingPattern);
    if (headingMatch) {
      return {
        complete: true,
        type: 'heading',
        markdown: headingMatch[0],
        remaining: text.slice(headingMatch[0].length)
      };
    }

    // 3. Horizontal rule: ---, ***, ___
    const hrPattern = /^([-*_]){3,}\n/;
    const hrMatch = text.match(hrPattern);
    if (hrMatch) {
      return {
        complete: true,
        type: 'hr',
        markdown: hrMatch[0],
        remaining: text.slice(hrMatch[0].length)
      };
    }

    // 4a. Unordered list item: - Item\n or * Item\n or + Item\n
    //     Includes continuation lines (indented lines following the marker line)
    const unorderedListPattern = /^[*\-+] .+?\n(?:[ \t]+\S.*\n)*/;
    const unorderedMatch = text.match(unorderedListPattern);
    if (unorderedMatch) {
      return {
        complete: true,
        type: 'list_item',
        markdown: unorderedMatch[0],
        remaining: text.slice(unorderedMatch[0].length)
      };
    }

    // 4b. Ordered list item: 1. Item\n or 2) Item\n
    //     Includes continuation lines (indented lines following the marker line)
    const orderedListPattern = /^\d+[.)] .+?\n(?:[ \t]+\S.*\n)*/;
    const orderedMatch = text.match(orderedListPattern);
    if (orderedMatch) {
      return {
        complete: true,
        type: 'list_item',
        markdown: orderedMatch[0],
        remaining: text.slice(orderedMatch[0].length)
      };
    }

    // 5. Blockquote: > Text\n\n
    const blockquotePattern = /^> .+?\n\n/s;
    const quoteMatch = text.match(blockquotePattern);
    if (quoteMatch) {
      return {
        complete: true,
        type: 'blockquote',
        markdown: quoteMatch[0],
        remaining: text.slice(quoteMatch[0].length)
      };
    }

    // 6. Paragraph: Text ending with \n\n, OR text terminated by the start
    //    of a registered structured tag. The second terminator removes an
    //    unwritten invariant that structured tags must begin the buffer —
    //    when the LLM glues an <l2-closing-summary> (or any registered tag) onto
    //    the end of prose without a blank-line separator, the paragraph
    //    matcher now flushes the prose up to the tag boundary, leaving the
    //    tag at position 0 where the structured-pattern branch (case 0) or
    //    incomplete-tag guard (case 0b) picks it up on the next iteration.
    //
    //    Streaming safety: the lookahead uses `(?:>|$)` so a partially
    //    streamed tag like `<l2-clos` at end-of-buffer still triggers the
    //    paragraph flush; the resulting remaining buffer starts with the
    //    partial tag and the existing incomplete-tag guard waits for more
    //    chunks. Unregistered literals like `<code>` or `<int x>` are
    //    invisible to the lookahead and pass through as normal prose.
    const paragraphPattern = this._paragraphPattern || (this._paragraphPattern = (() => {
      const tagNames = [...structuredBlockRenderers.keys()]
        .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
      return new RegExp(`^.+?(?:\\n\\n|(?=<(?:${tagNames})(?:>|$)))`, 's');
    })());
    const paraMatch = text.match(paragraphPattern);
    if (paraMatch && paraMatch[0].length > 0) {
      return {
        complete: true,
        type: 'paragraph',
        markdown: paraMatch[0],
        remaining: text.slice(paraMatch[0].length)
      };
    }

    // 7. Incomplete - need more chunks
    return {
      complete: false,
      type: null,
      markdown: '',
      remaining: text
    };
  }

  /**
   * Render a block with optional CSS-based stagger animation
   * @param {Object} block - {type, markdown}
   * @private
   */
  _renderBlock(block) {
    // Skip empty blocks
    if (!block.markdown.trim()) return;

    // Route structured blocks to component renderer
    if (block.type === 'structured') {
      this._renderStructuredBlock(block);
      return;
    }

    // Use existing markdown renderer (already configured with breaks:true, GFM, etc.)
    const cleanHtml = renderMarkdown(block.markdown);

    // Convert HTML string to DOM element(s)
    // marked.parse() can produce multiple sibling elements (e.g., <p> + <ul> when
    // text transitions to a list without a blank line separator), so we must
    // iterate all children — not just firstElementChild.
    const template = document.createElement('template');
    template.innerHTML = cleanHtml;
    const elements = [...template.content.children];

    if (elements.length === 0) {
      // Reset list-merge state when a block produces no DOM output
      // (prevents stale state from causing incorrect merges)
      this.lastListElement = null;
      this.lastBlockType = null;
      return;
    }

    for (const element of elements) {
      element.classList.add('message__block');

      if (this.isListElement(element) && this.shouldMergeWithPreviousList(element)) {
        this.appendToExistingList(element);
      } else {
        this.appendNewBlock(element);
      }
    }
  }

  /**
   * Render a structured content block via the component renderer registry
   * Bypasses markdown/DOMPurify — creates DOM elements directly from parsed content
   * @param {Object} block - {type: 'structured', tagName, innerContent, markdown}
   * @private
   */
  _renderStructuredBlock(block) {
    const renderer = structuredBlockRenderers.get(block.tagName);
    if (!renderer) return;

    try {
      const element = renderer(block.innerContent);
      if (!element) return;

      element.classList.add('message__block', 'message__structured-block');
      this.container.appendChild(element);

      // Reset list merge state (structured block breaks list continuity)
      this.lastListElement = null;
      this.lastBlockType = null;

      this.fadeInBlock(element);

      // Self-timed cards keep animating after their own fade-in — hold every
      // later block's reveal out by that amount (see structuredBlockHoldMs).
      const getHoldMs = structuredBlockHoldMs.get(block.tagName);
      if (getHoldMs) {
        this._extendHold(getHoldMs());
      }
    } catch (err) {
      console.error(`[BlockStreamBuffer] Structured block render failed for <${block.tagName}>:`, err);
      // Fallback: render inner content as a plain markdown paragraph
      this._renderBlock({ type: 'paragraph', markdown: block.innerContent });
    }
  }

  /**
   * Check if element is a list (<ul> or <ol>)
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  isListElement(element) {
    return element.tagName === 'UL' || element.tagName === 'OL';
  }

  /**
   * Check if this list should merge with previous list
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  shouldMergeWithPreviousList(element) {
    return (
      this.lastListElement &&
      this.lastListElement.tagName === element.tagName &&
      this.lastBlockType === 'list_item'
    );
  }

  /**
   * Extract all <li> from new list and append to existing list
   * Fade in only the new <li> items, not entire list
   * @param {HTMLElement} element - New list element to extract items from
   */
  appendToExistingList(element) {
    const listItems = element.querySelectorAll(':scope > li');
    for (const listItem of listItems) {
      this.lastListElement.appendChild(listItem);
      this.fadeInBlock(listItem);
    }
  }

  /**
   * Append new block and track if it's a list.
   * @param {HTMLElement} element
   */
  appendNewBlock(element) {
    this.container.appendChild(element);

    if (this.isListElement(element)) {
      this.lastListElement = element;
      this.lastBlockType = 'list_item';
    } else {
      this.lastListElement = null;
      this.lastBlockType = null;
    }

    this.fadeInBlock(element);
  }

  /**
   * Push every later block's reveal out by `ms` — called after rendering a
   * self-timed structured block (see structuredBlockHoldMs). The index-based
   * stagger path re-derives each block's delay from `this._extraDelay` at
   * render time; the timestamp-based min-spacing path has no such recompute,
   * so the hold is baked directly into `_lastBlockStart` instead.
   * @param {number} ms
   */
  _extendHold(ms) {
    if (!ms) return;
    this._extraDelay += ms;
    if (this.config.staggerDelay === 0 && this._lastBlockStart != null) {
      this._lastBlockStart += ms;
    }
  }

  /**
   * Apply fade-in animation to element
   * For staggerDelay > 0, applies CSS animation-delay based on block index
   * First block also gets staggerDelay (not instant) for consistent visual rhythm
   * @param {HTMLElement} element
   */
  fadeInBlock(element) {
    // Signal a rendered block (waiting→bars flip). Deliberately unguarded by
    // blockIndex: Case B re-arms the waiting phase mid-turn during tool
    // pauses, and every subsequent block must be able to flip it back to bars
    // — a blockIndex === 0 guard would make the flip a one-way door per turn.
    if (this.onBlockRender) {
      this.onBlockRender();
    }

    // Use (blockIndex + 1) so first block also has staggerDelay, not instant appearance
    let delay;
    if (this.config.staggerDelay > 0) {
      delay = (this.blockIndex + 1) * this.config.staggerDelay + this._extraDelay;
    } else {
      // Min-spacing path: ensure no two blocks start their fade-in closer together
      // than minBlockSpacing ms. For naturally-spaced arrivals (>minBlockSpacing
      // apart), delay === 0 and behavior matches prior implementation.
      const now = performance.now();
      const earliestStart = (this._lastBlockStart ?? -Infinity) + this.config.minBlockSpacing;
      const actualStart = Math.max(now, earliestStart);
      delay = actualStart - now;
      this._lastBlockStart = actualStart;
    }

    fadeIn(element, {
      duration: this.config.fadeInDuration,
      easing: this.config.fadeInEasing,
      slideDistance: this.config.slideDistance,
      delay: delay
    });

    // Increment block index for next block's stagger delay
    this.blockIndex++;
  }

  /**
   * Force-flush incomplete buffer as paragraph
   * Used when component interrupts text flow or at finalization
   * @returns {boolean} Whether any content was flushed
   */
  flushIncompleteBuffer() {
    if (!this.buffer.trim()) {
      return false;
    }

    this._renderBlock({
      type: 'paragraph',
      markdown: this.buffer
    });

    this.buffer = '';
    return true;
  }

  /**
   * Finalize stream - render any remaining buffered text
   */
  finalize() {
    this.flushIncompleteBuffer();
  }

  /**
   * Get current buffer content (for debugging)
   * @returns {string}
   */
  getBufferContent() {
    return this.buffer;
  }

  /**
   * Whether any self-timed structured block (see structuredBlockHoldMs) has
   * rendered in this message — i.e. whether getTotalAnimationDuration()'s
   * extra time is covering a still-animating card rather than just the
   * ordinary per-block fade-in cascade. Callers use this to decide whether a
   * "still working" indicator (e.g. shimmer bars) belongs under the message
   * for that stretch.
   * @returns {boolean}
   */
  hasSelfTimedHold() {
    return this._extraDelay > 0;
  }

  /**
   * Get the total animation duration for all rendered blocks
   * Useful for knowing when to show components after staggered text
   * @returns {number} Total duration in ms (last block delay + fade duration)
   */
  getTotalAnimationDuration() {
    if (this.blockIndex === 0) return this.config.fadeInDuration;
    if (this.config.staggerDelay > 0) {
      // With delay = (blockIndex + 1) * staggerDelay, last block (index N-1) starts at N * staggerDelay
      return this.blockIndex * this.config.staggerDelay + this.config.fadeInDuration + this._extraDelay;
    }
    // Min-spacing path: remaining time until last-scheduled block starts, plus fade
    const now = performance.now();
    const remaining = Math.max(0, (this._lastBlockStart ?? now) - now);
    return remaining + this.config.fadeInDuration;
  }
}
